import { Checkbox, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import { ProposalProduct } from "generated/graphql";
import { Controller, useFormContext } from "react-hook-form";
import { createPortal } from "react-dom";
import { Draggable, DraggableProvidedDragHandleProps, Droppable } from "react-beautiful-dnd";
import { draftColors } from "./draftDesignTokens";
import { getProductDisplayName, StageOrderingState } from "./stageOrderingState";

type ArrangeGroupItemProps = {
  groupIndex: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
};

export function groupDroppableId(index: number) {
  return `group-${index}`;
}

export default function ArrangeGroupItem({
  groupIndex,
  collapsed,
  onToggleCollapse,
  dragHandleProps,
}: ArrangeGroupItemProps) {
  const { control, watch } = useFormContext<StageOrderingState>();
  const group = watch(`items.${groupIndex}.group`);

  if (!group) return null;

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap="8px" px="12px" py="8px">
        <MDBox
          {...dragHandleProps}
          sx={{ cursor: "grab", display: "flex", color: draftColors.textSecondary }}
        >
          <Icon fontSize="small">drag_indicator</Icon>
        </MDBox>
        <MDBox
          sx={{ cursor: "pointer", display: "flex", color: draftColors.textSecondary }}
          onClick={onToggleCollapse}
        >
          <Icon fontSize="small">{collapsed ? "chevron_right" : "expand_more"}</Icon>
        </MDBox>
        <Controller
          control={control}
          name={`items.${groupIndex}.group.name`}
          rules={{ required: "Group name is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              placeholder="Group name"
              variant="standard"
              size="small"
              error={!!fieldState.error || !field.value?.trim()}
              sx={{
                flex: 1,
                "& .MuiInput-input": {
                  fontSize: "13px",
                  fontWeight: 600,
                  color: draftColors.textPrimary,
                  py: "2px",
                },
                "& .MuiInput-underline:before": { borderColor: draftColors.border },
              }}
            />
          )}
        />
        <MDBox display="flex" alignItems="center" gap="4px">
          <Controller
            control={control}
            name={`items.${groupIndex}.group.showIndividualProducts`}
            render={({ field }) => (
              <Checkbox
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
                onMouseDown={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0 }}
              />
            )}
          />
          <UiTypography
            uiVariant="body-sm"
            sx={{ fontSize: "11px", color: draftColors.textSecondary, whiteSpace: "nowrap" }}
          >
            Show products
          </UiTypography>
        </MDBox>
      </MDBox>

      {!collapsed && (
        <Droppable droppableId={groupDroppableId(groupIndex)}>
          {(provided) => (
            <MDBox
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                pl: "32px",
                pr: "12px",
                pb: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                minHeight: "32px",
              }}
            >
              {group.products.map((product: ProposalProduct, productIndex: number) => (
                <Draggable key={product.id} draggableId={product.id} index={productIndex}>
                  {(provided, snapshot) => {
                    const child = (
                      <MDBox
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        display="flex"
                        alignItems="center"
                        gap="8px"
                        px="8px"
                        py="4px"
                        sx={{
                          borderRadius: "6px",
                          backgroundColor: snapshot.isDragging
                            ? draftColors.chipBg
                            : "rgba(241, 245, 249, 0.5)",
                          border: `1px solid ${
                            snapshot.isDragging ? draftColors.blue : "transparent"
                          }`,
                        }}
                      >
                        <MDBox
                          {...provided.dragHandleProps}
                          sx={{ cursor: "grab", display: "flex", color: draftColors.textSecondary }}
                        >
                          <Icon sx={{ fontSize: "14px" }}>drag_indicator</Icon>
                        </MDBox>
                        <UiTypography uiVariant="body-sm" sx={{ fontSize: "12px" }}>
                          {getProductDisplayName(product)}
                        </UiTypography>
                      </MDBox>
                    );
                    return snapshot.isDragging ? createPortal(child, document.body) : child;
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </MDBox>
          )}
        </Droppable>
      )}
    </MDBox>
  );
}
